import { ResponsiveLine } from "@nivo/line";
import { colors } from "common/const/colors";
import { CustomChartTooltip } from "common/components/Etc/tooltip";
import { areaChartTheme } from "./ChartTheme.jsx";

export const AreaChart = ({ data }) => {
  const scaleSize = 50; //  기본 최대값 50
  const parsedData = data.map((series) => ({
    ...series,
    data: series.data.map((point) => ({
      x: point.key,
      y: point.value,
    })),
  }));

  const generateTickValues = (data) => {
    let maxY = 0;
    data.forEach((series) => {
      series.data.forEach((point) => {
        if (point.y > maxY) maxY = point.y;
      });
    });

    if (maxY === 0) return [0];

    const magnitude = Math.pow(10, Math.floor(Math.log10(maxY)));
    const step = Math.max(1, magnitude);
    const roundedMaxY = Math.ceil(maxY / step) * step;
    const tickValues = [];
    for (let i = 0; i <= roundedMaxY; i += step) {
      tickValues.push(i);
    }
    return tickValues;
  };

  /**
   * x축 tick 간격을 자동 계산하여 일정 간격으로 반환하는 함수
   * - 데이터가 많을수록 step이 커짐
   * - 항상 첫 번째와 마지막 tick 포함
   */
  const getTickValuesX = (parsedData) => {
    const points = parsedData?.[0]?.data ?? [];
    const total = points.length;

    if (total === 0) return [];

    // 표시할 tick 개수 (데이터 개수 대비 균등 분포)
    const targetTickCount = 10; // 원하는 간격 (조정 가능)
    const step = Math.max(1, Math.floor(total / targetTickCount));

    // step 단위로 균등하게 tick 추출
    const ticks = [];
    for (let i = 0; i < total; i += step) {
      ticks.push(points[i].x);
    }

    // 중복 제거 및 정렬
    return Array.from(new Set(ticks)).sort((a, b) => a.localeCompare(b));
  };

  const tickValues = generateTickValues(parsedData);
  const leftMargin =
    Math.floor(Math.log10(tickValues.at(-1) || 1) + 1) * 10 + 30;
  const yMax = parsedData.length > 0 ? tickValues.at(-1) : scaleSize;

  return (
    <ResponsiveLine
      data={parsedData}
      margin={{ top: 20, right: 50, bottom: 40, left: leftMargin }}
      yScale={{
        type: "linear",
        min: 0,
        max: yMax,
        stacked: false,
        reverse: false,
      }}
      axisLeft={{
        tickValues: tickValues,
        tickSize: 0,
        tickPadding: 30,
        tickRotation: 0,
      }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        tickValues: getTickValuesX(parsedData),
      }}
      colors={colors.MainBlue}
      defs={[
        {
          id: "area",
          type: "linearGradient",
          colors: [
            { offset: 0, color: colors.MainBlue, opacity: 1 }, // 위쪽 진하게
            { offset: 100, color: colors.MainBlue, opacity: 0 }, // 아래쪽 투명하게
          ],
        },
      ]}
      fill={[
        {
          match: "*",
          id: "area",
        },
      ]}
      crosshairType="none"
      enablePoints={false}
      enableGridX={false}
      enableGridY={false}
      enableArea={true}
      useMesh={true}
      animate={false}
      theme={areaChartTheme}
      tooltip={CustomChartTooltip}
      layers={[
        "grid",
        "markers",
        (props) => <CustomHorizontalLines {...props} lineValues={tickValues} />,
        "axes",
        "areas",
        "lines",
        "mesh",
        CustomCrosshair,
        CustomMeshPoints,
        "legends",
      ]}
    />
  );
};

const CustomHorizontalLines = ({ yScale, width, lineValues }) => {
  return (
    <>
      {lineValues.map((value) => {
        const isFirst = value === 0;
        return (
          <line
            key={`grid-line-${value}`}
            y1={yScale(value)}
            y2={yScale(value)}
            x1={-20}
            x2={width}
            stroke={isFirst ? colors.Black : colors.Gray}
            strokeWidth={1}
          />
        );
      })}
    </>
  );
};

const CustomMeshPoints = ({ currentPoint }) => {
  if (!currentPoint) return null;

  return (
    <circle
      key={currentPoint.id}
      cx={currentPoint.x}
      cy={currentPoint.y}
      r={areaChartTheme.dots.size}
      fill={areaChartTheme.dots.background}
      style={{ pointerEvents: "none" }}
    />
  );
};

const CustomCrosshair = ({ yScale, currentPoint }) => {
  if (!currentPoint) return null;

  return (
    <line
      x1={currentPoint.x}
      x2={currentPoint.x}
      y1={currentPoint.y}
      y2={yScale(0)}
      stroke={areaChartTheme.crosshair.line.stroke}
      strokeWidth={areaChartTheme.crosshair.line.strokeWidth}
      strokeDasharray={areaChartTheme.crosshair.line.strokeDasharray}
    />
  );
};
